import saveAs from "file-saver";
import React, { useContext, useRef, useState } from "react";
import { BASE_PATH } from "../..";
import { useProjectFolderServicePostProjectFolderUploadToKingfisherDam, useProjectServicePostProjectUploadToKingfisherDam } from '../../openapi/queries';
import { ApiError, ExcelValidationMessage, ProjectFolderViewDTO, ProjectViewDTO } from '../../openapi/requests';
import { isProject } from '../../pages/Projects';
import { Dialog } from '../layout/Dialog';
import './CreateProject.scss';
import BottomDrawer from '../layout/BottomDrawer';
import { DialogContext } from '../../contexts/DialogContext';
import { ReactComponent as LoadingIcon } from '../../assets/icons/loading.svg';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
const LoadingComponent = motion(LoadingIcon)

interface Props {
  isOpen: boolean;
  onClose: () => void;
  projectOrFolder: ProjectViewDTO | ProjectFolderViewDTO | undefined | null;
}

const UploadToDam: React.FC<Props> = ({ isOpen, onClose, projectOrFolder }) => {
  const hiddenFileInput = useRef<HTMLInputElement>(null);
  const [showUploadError, setShowUploadError] = useState(false);
  const [uploadErrors, setUploadErrors] = useState<ExcelValidationMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const { mutateAsync: uploadFolderAsync, isPending: uploadingFolder } = useProjectFolderServicePostProjectFolderUploadToKingfisherDam();
  const { mutateAsync: uploadProjectAsync, isPending: uploadingProject } = useProjectServicePostProjectUploadToKingfisherDam();
  const { message } = useContext(DialogContext);

  const downloadDamSheet = () => {
    if (projectOrFolder) {
      setLoading(true);
      const token = localStorage.getItem("accessToken");
      const url =
         isProject(projectOrFolder)
          ? `${BASE_PATH}/Project/${projectOrFolder?.id}/kingfisherDAMSheet`
          : `${BASE_PATH}/Projectfolder/${projectOrFolder?.id}/kingfisherDAMSheet`;
      let filename = "";

      fetch(url, {
        method: "get",
        headers: {
          Authorization: "Bearer " + token,
        },
      })
        .then((res) => {
          const header = res.headers.get("Content-Disposition");
          setLoading(false);
          if (header) {
            const parts = header.split(";");
            filename = parts[1].split("=")[1];
            filename = filename.replaceAll('"', "");
          }
          return res.blob();
        })
        .then((blob) => {
          saveAs(blob, filename);
        });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && projectOrFolder) {
      const file = e.target.files[0];

      if (isProject(projectOrFolder)) {
        uploadProjectAsync({ key: projectOrFolder.id, formData: { file } })
          .then(() => {
            message("File uploaded", "Excel file uploaded, and images added to uploaded queue");
          })
          .catch((reason: ApiError) => {
            setShowUploadError(true);
            if (Array.isArray(reason.body)) {
              setUploadErrors(reason.body as ExcelValidationMessage[]);
            } else {
              setUploadErrors([{ message: JSON.stringify(reason.body), line: undefined }]);
            }
          });
      } else {
        uploadFolderAsync({ key: projectOrFolder.id, formData: { file } })
          .then(() => {
            message("Transfer started", "Files are being transfered to the DAM now");
          })
          .catch((reason: ApiError) => {
            setShowUploadError(true);
            if (Array.isArray(reason.body)) {
              setUploadErrors(reason.body as ExcelValidationMessage[]);
            } else {
              setUploadErrors([{ message: JSON.stringify(reason.body), line: undefined }]);
            }
          });
      }
    }
  };

  const handleUpload = () => {
    if (hiddenFileInput.current) {
      hiddenFileInput.current.click();
    }
  };

    return (
        <>
            <BottomDrawer open={isOpen} onClose={onClose} size={550}>
                <div className='wizard'>
                    <div className='wizard-step'>
                        <h1>1</h1>
                        <button className='primary button' onClick={downloadDamSheet} disabled={loading}>
                            {loading ? <LoadingComponent /> : 'Download DAM excel sheet'}
                            </button>
                    </div>

                    <div className='wizard-step'>
                        <h1>2</h1>
                        <input id="file" type="file" onChange={handleFileChange} style={{ display: 'none' }} ref={hiddenFileInput} />
                        <button className='primary button' onClick={handleUpload} disabled={uploadingFolder || uploadingProject}>Upload Excel</button>
                    </div>

                    <div className='wizard-step' style={{flexGrow: 2}}>
                        <h2>Uploading to the DAM – Quick Guide</h2>
                        <p>We’ve made it easier to upload your files to the DAM by automating the transfer process.<br />
                        But there are still a couple of small steps you need to take to make sure everything goes smoothly.</p>
                        <p><small><b>Here’s what you need to do:</b></small></p>
                        <ol>
                            <li>
                                <b>Download the Excel file</b> – it contains a list of the image files you’re working with.
                                <p>Open and edit the Excel file – make sure:</p>
                                <ul>
                                    <li>The file names are correct</li>
                                    <li>The metadata (like titles, descriptions, etc.) is filled in properly</li>
                                </ul>
                            </li>
                            <li><b>Upload the updated Excel file</b> to CoD.</li>
                        </ol>
                        <p>
                            That’s it!<br />
                            Once that’s done, CoD takes care of the rest:<br />
                            Your files are automatically uploaded to the DAM, processed, and auto-approved to make sure they end up in the right place.
                        </p>
                        <p>
                            <b>Need more details?</b><br />
                            <Link to={'/create/help#project-overview--dam-upload'}>Check out the full walkthrough here</Link>
                        </p>
                    </div>
                </div>
            </BottomDrawer>
            <Dialog open={showUploadError} className='confirm'>
                <h1>The following errors where found</h1>
                <p className='confirm-body'>
                    {uploadErrors.map(e => <p key={e.line}>
                        {e.line ? `line ${e.line}: ` : ''}{e.message}
                    </p>)}
                </p>

        <div className="confirm-footer">
          <button className="confirm" onClick={() => setShowUploadError(false)}>
            Ok
          </button>
        </div>
      </Dialog>
    </>
  );
};

export default UploadToDam;
